import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar, { SIDEBAR_WIDTH } from "../components/layout/Sidebar";
import { TYPE, FONT_FAMILY, OVERVIEW } from "../constants/theme";
import Description from "../components/typography/Description";
import OverviewSection from "../components/overview/OverviewSection";
import SectionHeader from "../components/overview/SectionHeader";
import SectionNav from "../components/overview/SectionNav";
import { useTour } from "../tour/TourContext";

const LC_SECTIONS = [
  { id: "lc-overview", label: "Overview" },
  { id: "lc-explorer", label: "Explorer" },
  { id: "lc-distribution", label: "Workflow Distribution" },
  { id: "lc-outliers", label: "Outliers" },
  { id: "lc-recently-closed", label: "Recently Closed" },
  { id: "lc-assistant", label: "AI Assistant" },
];
const LC_ANCHOR = { scrollMarginTop: 72 };

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const JIRA = "https://ecosense-enviro.atlassian.net/browse";

const G = {
  white: "#fff",
  gray50: "#F9FAFB", gray100: "#F3F4F6", gray200: "#E5E7EB",
  gray300: "#D1D5DB", gray400: "#9CA3AF", gray500: "#6B7280",
  gray600: "#4B5563", gray700: "#374151", gray800: "#1F2937", gray900: "#111827",
  green50: "#F0FDF4", green100: "#DCFCE7", green200: "#BBF7D0",
  green500: "#22C55E", green600: "#16A34A", green700: "#15803D", green800: "#166534",
  amber50: "#FFFBEB", amber100: "#FEF3C7", amber600: "#D97706", amber700: "#B45309",
  red50: "#FFF1F2", red100: "#FFE4E6", red700: "#B91C1C",
  blue50: "#EFF6FF", blue600: "#2563EB", blue700: "#1D4ED8",
  border: "#E5E7EB",
};

// ─── UTILITIES ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return "—"; }
}

function categorizeStage(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("done") || s.includes("closed") || s.includes("resolved") || s.includes("complete") || s.includes("deployed"))
    return "done";
  if (s.includes("review") || s.includes("pr") || s.includes("qa") || s.includes("test") || s.includes("verif") || s.includes("staging"))
    return "review";
  if (s.includes("progress") || s.includes("development") || s.includes("dev") || s.includes("active") || s.includes("started"))
    return "active";
  return "backlog";
}

function ageColor(days) {
  if (days == null) return { bg: "rgba(255,255,255,0.06)", c: "rgba(255,255,255,0.28)" };
  if (days > 21) return { bg: "rgba(239,68,68,0.2)", c: "#FCA5A5" };
  if (days > 10) return { bg: "rgba(251,191,36,0.15)", c: "#FCD34D" };
  return { bg: "rgba(255,255,255,0.07)", c: "rgba(255,255,255,0.4)" };
}

function statusCol(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("done") || s.includes("closed") || s.includes("resolved"))
    return { bar: "#86EFAC", bg: "rgba(34,197,94,0.15)", c: "#86EFAC" };
  if (s.includes("progress") || s.includes("development") || s.includes("review") || s.includes("pr") || s.includes("test") || s.includes("qa") || s.includes("block"))
    return { bar: "#93C5FD", bg: "rgba(59,130,246,0.2)", c: "#93C5FD" };
  return { bar: "rgba(255,255,255,0.2)", bg: "rgba(255,255,255,0.06)", c: "rgba(255,255,255,0.4)" };
}

function priorityCol(priority) {
  const p = (priority || "").toLowerCase();
  if (p === "critical") return { bg: "rgba(239,68,68,0.2)", c: "#FCA5A5" };
  if (p === "high" || p === "highest") return { bg: "rgba(59,130,246,0.22)", c: "#93C5FD" };
  if (p === "medium") return { bg: "rgba(59,130,246,0.1)", c: "#BFDBFE" };
  return { bg: "rgba(255,255,255,0.06)", c: "rgba(255,255,255,0.3)" };
}

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────────

function card(extra) {
  return { background: G.white, border: `1px solid ${G.border}`, borderRadius: 0, padding: 20, ...extra };
}

function PriorityBadge({ priority }) {
  const col = priorityCol(priority);
  return (
    <span style={{ background: col.bg, color: col.c, fontSize: 11, fontWeight: 600, letterSpacing: "0.02em", padding: "2px 8px", borderRadius: 0, whiteSpace: "nowrap" }}>
      {priority || "—"}
    </span>
  );
}

function StatusBadge({ status }) {
  const col = statusCol(status);
  return (
    <span style={{ background: col.bg, color: col.c, fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 0, whiteSpace: "nowrap" }}>
      {status || "—"}
    </span>
  );
}

function Loading() {
  return <div style={{ padding: 32, textAlign: "center", color: G.gray400, fontSize: 13 }}>Loading…</div>;
}

// Section-level "View Score Breakdown" disclosure — explains factors/weights
// behind a score, ranking, or classification. Per-ticket breakdowns already
// shown elsewhere in the UI are left exactly as implemented.
function ScoreBreakdown({ title, intro, factors = [], footnote, dark, style = {} }) {
  const [open, setOpen] = useState(false);
  const fg = dark ? "rgba(255,255,255,0.55)" : G.gray500;
  const fgHover = dark ? "#FFFFFF" : G.gray900;
  const border = dark ? "1px solid rgba(255,255,255,0.15)" : `1px solid ${G.border}`;
  const panelBg = dark ? "rgba(255,255,255,0.05)" : G.gray50;
  const panelBorder = dark ? "1px solid rgba(255,255,255,0.1)" : `1px solid ${G.border}`;
  const headColor = dark ? "rgba(255,255,255,0.85)" : G.gray800;
  const bodyColor = dark ? "rgba(255,255,255,0.55)" : G.gray600;
  const mutedColor = dark ? "rgba(255,255,255,0.3)" : G.gray400;
  return (
    <div style={style}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ background: "transparent", border, borderRadius: 0, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: fg, cursor: "pointer", letterSpacing: "0.02em", whiteSpace: "nowrap", fontFamily: "inherit" }}
        onMouseEnter={e => { e.currentTarget.style.color = fgHover; }}
        onMouseLeave={e => { e.currentTarget.style.color = fg; }}
      >
        {open ? "Hide Score Breakdown ▲" : "View Score Breakdown ▾"}
      </button>
      {open && (
        <div style={{ marginTop: 10, padding: "14px 16px", background: panelBg, border: panelBorder, maxWidth: 480 }}>
          {title && <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: mutedColor, marginBottom: 8 }}>{title}</div>}
          {intro && <div style={{ fontSize: 12, color: bodyColor, lineHeight: 1.55, marginBottom: 10 }}>{intro}</div>}
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {factors.map((f, i) => (
              <li key={i} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span style={{ color: bodyColor }}>{f.label}</span>
                <span style={{ fontWeight: 700, color: headColor, fontFamily: TYPE.mono.fontFamily, fontSize: 12, flexShrink: 0 }}>{f.points}</span>
              </li>
            ))}
          </ul>
          {footnote && (
            <div style={{ fontSize: 11, color: mutedColor, marginTop: 10, lineHeight: 1.5, borderTop: dark ? "1px solid rgba(255,255,255,0.08)" : `1px solid ${G.border}`, paddingTop: 10 }}>
              {footnote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ value, color, bg, h }) {
  return (
    <div style={{ height: h, background: bg || G.gray100, borderRadius: 0, overflow: "hidden" }}>
      <div style={{ height: h, width: `${Math.min(100, Math.max(0, value))}%`, background: color, borderRadius: 0 }} />
    </div>
  );
}

// ─── PAGE HEADER ─────────────────────────────────────────────────────────────
function PageHeader() {
  const [time, setTime] = useState(new Date());
  const { start: startTour } = useTour();
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const FF = FONT_FAMILY;

  return (
    <OverviewSection background="dark" style={{ padding: "60px 0 68px", fontFamily: FF }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 48, flexWrap: "wrap" }}>

        {/* LEFT 70% */}
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          {/* Breadcrumb eyebrow — tertiary */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
            {["OpsIntel", "Analytics", "Lifecycle Intelligence"].map((b, i, arr) => (
              <span key={b} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ ...OVERVIEW.eyebrow, color: i === arr.length - 1 ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)" }}>{b}</span>
                {i < arr.length - 1 && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>/</span>}
              </span>
            ))}
          </div>

          {/* Hero title — dominant */}
          <div style={{ fontSize: 52, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.04, color: "#FFFFFF", marginBottom: 16 }}>
            Lifecycle Intelligence
          </div>

          {/* Description — readable */}
          <div style={{ ...OVERVIEW.support, color: "rgba(255,255,255,0.6)", maxWidth: 560, marginBottom: 36 }}>
            Full-spectrum visibility into how tickets move through your workflow — from creation to resolution.
          </div>

          {/* Sub-view labels */}
          <div style={{ display: "flex", flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 22 }}>
            {[
              { label: "Workflow Distribution" },
              { label: "Ticket Journey" },
              { label: "Ownership Analysis" },
              { label: "Resolution Patterns" },
            ].map((m, i, arr) => (
              <div key={m.label} style={{
                paddingRight: i < arr.length - 1 ? 28 : 0,
                marginRight: i < arr.length - 1 ? 28 : 0,
                borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
              }}>
                <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.4)" }}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT 30% */}
        <div style={{ flex: "1 1 280px", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* User Guide — starts the Lifecycle walkthrough */}
          <button
            type="button"
            onClick={() => startTour("lifecycle")}
            title="Take a guided tour of the Lifecycle module"
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 13px",
              borderRadius: 0,
              border: "1px solid rgba(147,197,253,0.35)",
              background: "rgba(37,99,235,0.14)",
              color: "#93C5FD",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: FF,
              cursor: "pointer",
              letterSpacing: "0.02em",
              transition: "background 0.15s, border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(37,99,235,0.24)";
              e.currentTarget.style.borderColor = "rgba(147,197,253,0.6)";
              e.currentTarget.style.color = "#FFFFFF";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(37,99,235,0.14)";
              e.currentTarget.style.borderColor = "rgba(147,197,253,0.35)";
              e.currentTarget.style.color = "#93C5FD";
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "1.5px solid currentColor",
                fontSize: 9,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              ?
            </span>
            User Guide
          </button>

          <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.35)", marginBottom: 4 }}>
            Data Sync
          </div>

          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, padding: "18px 20px" }}>
            <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>Last Synced</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.02em" }}>
              {time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#93C5FD" }} />
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>Live connection active</span>
            </div>
          </div>

          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, padding: "18px 20px" }}>
            <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>Coverage</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}>
              All sprints · All assignees<br />All workflow stages
            </div>
          </div>
        </div>
      </div>
    </OverviewSection>
  );
}

// ─── TICKET LIFECYCLE PANEL ───────────────────────────────────────────────────
function TicketLifecyclePanel({ data, loading, prs, prsLoading }) {
  if (loading) return (
    <div style={{ padding: "16px 32px", color: "rgba(255,255,255,0.3)", fontSize: 12 }}>Loading lifecycle data…</div>
  );
  if (!data) return (
    <div style={{ padding: "16px 32px", color: "rgba(255,255,255,0.25)", fontSize: 12 }}>No lifecycle history available for this ticket.</div>
  );

  const maxAging = Math.max(...Object.values(data.stageAgingDays), 0.1);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", padding: "20px 32px 24px", background: "#0B223F", borderTop: "1px solid rgba(255,255,255,0.06)" }}>

      {/* Journey */}
      <div style={{ paddingRight: 24, borderRight: "1px solid rgba(255,255,255,0.06)", minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
          Ticket Journey{data.bounceCount > 0 ? ` · ${data.bounceCount} bounce${data.bounceCount > 1 ? "s" : ""}` : ""}
        </div>
        <Description light style={{ marginBottom: 10 }}>Every status this ticket passed through. ↩ means it moved backward.</Description>
        {data.journey.length === 0
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>No transition data</div>
          : <>
            {data.isEstimated && (
              <div title="This date/duration wasn't directly recorded — it's inferred from nearby data points." style={{ fontSize: 10, fontWeight: 700, color: "rgba(251,191,36,0.8)", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.2)", padding: "2px 6px", marginBottom: 8, letterSpacing: "0.07em", display: "inline-block" }}>
                ESTIMATED
              </div>
            )}
            <div className="themed-scroll-dark" style={{ maxHeight: "min(40vh, 280px)", overflowY: "auto" }}>
              {data.journey.map((step, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", opacity: data.isEstimated ? 0.75 : 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span title={step.isBounce ? "Moved backward in the workflow" : step.isCurrent ? "Current stage" : undefined} style={{
                      fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 2,
                      background: step.isBounce ? "rgba(239,68,68,0.2)" : step.isCurrent ? "rgba(34,197,94,0.18)" : "rgba(255,255,255,0.07)",
                      color: step.isBounce ? "#FCA5A5" : step.isCurrent ? "#86EFAC" : "rgba(255,255,255,0.55)",
                    }}>
                      {step.status}{step.isBounce ? " ↩" : ""}{step.isCurrent ? " ●" : ""}
                    </span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>{data.isEstimated ? "~" : ""}{step.durationDays}d</span>
                  </div>
                  {i < data.journey.length - 1 && (
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.12)", marginLeft: 6, lineHeight: 1.4 }}>↓</div>
                  )}
                </div>
              ))}
            </div>
          </>
        }
      </div>

      {/* Stage Aging */}
      <div style={{ padding: "0 24px", borderRight: "1px solid rgba(255,255,255,0.06)", minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Stage Aging</div>
        <Description light style={{ marginBottom: 10 }}>Days spent in each status the ticket has passed through.</Description>
        {Object.keys(data.stageAgingDays).length === 0 && data.resolvedDaysAgo == null
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>No stage data</div>
          : (
            <>
              {data.isEstimated && (
                <div title="This date/duration wasn't directly recorded — it's inferred from nearby data points." style={{ fontSize: 10, fontWeight: 700, color: "rgba(251,191,36,0.8)", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.2)", padding: "2px 6px", marginBottom: 8, letterSpacing: "0.07em", display: "inline-block" }}>
                  ESTIMATED
                </div>
              )}
              <div className="themed-scroll-dark" style={{ maxHeight: "min(40vh, 280px)", overflowY: "auto" }}>
                {Object.entries(data.stageAgingDays)
                  .sort((a, b) => b[1] - a[1])
                  .map(([stage, days]) => (
                    <div key={stage} style={{ marginBottom: 10, opacity: data.isEstimated ? 0.75 : 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{stage}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.65)" }}>~{days}d</span>
                      </div>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.07)" }}>
                        <div style={{ height: 3, width: `${(days / maxAging) * 100}%`, background: "#93C5FD" }} />
                      </div>
                    </div>
                  ))
                }
                {data.resolvedDaysAgo != null && (
                  <div style={{ marginTop: Object.keys(data.stageAgingDays).length > 0 ? 10 : 0, padding: "6px 8px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.18)", borderRadius: 2 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#86EFAC", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 2 }}>Resolved</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
                      {data.isEstimated ? `~${data.resolvedDaysAgo}d ago (est.)` : `Completed ${data.resolvedDaysAgo}d ago`}
                    </div>
                  </div>
                )}
              </div>
            </>
          )
        }
      </div>

      {/* Ownership Timeline */}
      <div style={{ padding: "0 24px", borderRight: "1px solid rgba(255,255,255,0.06)", minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Ownership Timeline</div>
        <Description light style={{ marginBottom: 10 }}>Everyone assigned this ticket, and how long each one held it.</Description>
        {data.ownershipTimeline.length === 0
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>No ownership data</div>
          : <>
            {data.isOwnershipEstimated && (
              <div title="This date/duration wasn't directly recorded — it's inferred from nearby data points." style={{ fontSize: 10, fontWeight: 700, color: "rgba(251,191,36,0.8)", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.2)", padding: "2px 6px", marginBottom: 8, letterSpacing: "0.07em", display: "inline-block" }}>
                ESTIMATED
              </div>
            )}
            <div className="themed-scroll-dark" style={{ maxHeight: "min(40vh, 280px)", overflowY: "auto" }}>
              {data.ownershipTimeline.map((o, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", opacity: data.isOwnershipEstimated ? 0.75 : 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: o.isCurrent ? "#86EFAC" : "rgba(255,255,255,0.55)" }}>{o.owner}</span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>~{o.durationDays}d</span>
                    {o.isCurrent && <span style={{ fontSize: 10, background: "rgba(34,197,94,0.15)", color: "#86EFAC", padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.05em" }}>CURRENT</span>}
                  </div>
                  {i < data.ownershipTimeline.length - 1 && (
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.12)", marginLeft: 2, lineHeight: 1.4 }}>↓</div>
                  )}
                </div>
              ))}
            </div>
          </>
        }
      </div>

      {/* GitHub Linked PRs */}
      <div style={{ paddingLeft: 24, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Linked PRs</div>
        <Description light style={{ marginBottom: 10 }}>GitHub pull requests whose branch or description references this ticket.</Description>
        {prsLoading
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>Loading…</div>
          : !prs
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>–</div>
          : prs.length === 0
          ? <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>No linked PRs</div>
          : (
            <div className="themed-scroll-dark" style={{ maxHeight: "min(40vh, 280px)", overflowY: "auto" }}>
              {prs.map((pr, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>{pr.repository}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <a href={pr.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 700, color: "#93C5FD", textDecoration: "none" }}>
                      #{pr.prNumber}
                    </a>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 2,
                      background: pr.merged ? "rgba(139,92,246,0.2)" : pr.state === "open" ? "rgba(34,197,94,0.18)" : "rgba(255,255,255,0.07)",
                      color: pr.merged ? "#C4B5FD" : pr.state === "open" ? "#86EFAC" : "rgba(255,255,255,0.4)",
                      letterSpacing: "0.04em",
                    }}>
                      {pr.merged ? "MERGED" : pr.state === "open" ? "OPEN" : "CLOSED"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>
                    {pr.prTitle}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div>

    </div>
  );
}

// ─── 1. LIFECYCLE EXPLORER ────────────────────────────────────────────────────
function LifecycleExplorer() {
  const [statusOptions, setStatusOptions] = useState([]);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const timer = useRef(null);
  const syncMsgTimer = useRef(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [lifecycleCache, setLifecycleCache] = useState({});
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [prCache, setPrCache] = useState({});
  const [prLoading, setPrLoading] = useState(false);
  const [sprintSyncing, setSprintSyncing] = useState(false);
  const [sprintSyncMsg, setSprintSyncMsg] = useState(null);

  const toggleTicket = (ticketNo) => {
    if (expandedTicket === ticketNo) { setExpandedTicket(null); return; }
    setExpandedTicket(ticketNo);
    if (lifecycleCache[ticketNo] === undefined) {
      setLifecycleLoading(true);
      fetch(`${API}/lifecycle/ticket/${ticketNo}`)
        .then(r => r.json())
        .then(d => { setLifecycleCache(prev => ({ ...prev, [ticketNo]: d.error ? null : d })); setLifecycleLoading(false); })
        .catch(() => { setLifecycleCache(prev => ({ ...prev, [ticketNo]: null })); setLifecycleLoading(false); });
    }
    if (prCache[ticketNo] === undefined) {
      setPrLoading(true);
      fetch(`${API}/github/linking/ticket/${ticketNo}`)
        .then(r => r.json())
        .then(d => { setPrCache(prev => ({ ...prev, [ticketNo]: d.linkedPRs || [] })); setPrLoading(false); })
        .catch(() => { setPrCache(prev => ({ ...prev, [ticketNo]: [] })); setPrLoading(false); });
    }
  };

  useEffect(() => {
    fetch(`${API}/lifecycle/workflow-distribution`)
      .then(r => r.json())
      .then(d => setStatusOptions((d.statuses || []).map(s => s.status)))
      .catch(() => {});
    fetch(`${API}/workload/developer-list`)
      .then(r => r.json())
      .then(d => setDeveloperOptions(d.developers || []))
      .catch(() => {});
  }, []);

  const doFetch = useCallback((s, sf, pf, af, offset) => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "50", offset: String(offset) });
    if (s.trim()) params.set("search", s.trim());
    if (sf) params.set("status", sf);
    if (pf) params.set("priority", pf);
    if (af) params.set("assignee", af);
    fetch(`${API}/lifecycle/explorer?${params}`)
      .then(r => r.json())
      .then(d => {
        if (offset === 0) setTickets(d.tickets || []);
        else setTickets(prev => [...prev, ...(d.tickets || [])]);
        setTotal(d.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const syncSprints = useCallback(() => {
    setSprintSyncing(true);
    clearTimeout(syncMsgTimer.current);
    setSprintSyncMsg(null);
    fetch(`${API}/jira/sync-sprints`)
      .then(r => r.json())
      .then(d => {
        const msg = d.success
          ? `Updated ${d.updated} tickets — ${d.withSprint} with sprint`
          : (d.reason || "Sync failed");
        setSprintSyncMsg(msg);
        syncMsgTimer.current = setTimeout(() => setSprintSyncMsg(null), 5000);
        if (d.success) {
          setPage(0);
          doFetch(search, statusFilter, priorityFilter, assigneeFilter, 0);
        }
      })
      .catch(() => {
        setSprintSyncMsg("Sync failed — check network or backend logs");
        syncMsgTimer.current = setTimeout(() => setSprintSyncMsg(null), 5000);
      })
      .finally(() => setSprintSyncing(false));
  }, [doFetch, search, statusFilter, priorityFilter, assigneeFilter]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPage(0);
      doFetch(search, statusFilter, priorityFilter, assigneeFilter, 0);
    }, 350);
    return () => clearTimeout(timer.current);
  }, [search, statusFilter, priorityFilter, assigneeFilter, doFetch]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    doFetch(search, statusFilter, priorityFilter, assigneeFilter, next * 50);
  };

  // Export the rows currently loaded in the table as a print-ready document.
  // Uses the browser's own "Save as PDF" — no dependency, no backend call.
  const exportPDF = useCallback(() => {
    if (tickets.length === 0) return;
    const esc = (s) => String(s ?? "—").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

    const filterParts = [];
    if (search.trim()) filterParts.push(`Search: “${esc(search.trim())}”`);
    if (statusFilter) filterParts.push(`Status: ${esc(statusFilter)}`);
    if (priorityFilter) filterParts.push(`Priority: ${esc(priorityFilter)}`);
    if (assigneeFilter) filterParts.push(`Assignee: ${esc(assigneeFilter)}`);
    const filterLine = filterParts.length ? filterParts.join("  ·  ") : "No filters applied";

    const rows = tickets.map((t) => `
      <tr>
        <td class="mono">${esc(t.ticketNo)}</td>
        <td class="sum">${esc(t.summary || "—")}</td>
        <td>${esc(t.priority || "—")}</td>
        <td>${esc(t.status || "—")}</td>
        <td>${esc(t.assignedTo || "—")}</td>
        <td>${esc(t.sprint || "—")}</td>
        <td class="nw">${esc(fmtDate(t.raisedOn))}</td>
        <td class="nw">${esc(fmtDate(t.updatedOn))}</td>
        <td class="nw">${t.ageingDays != null ? t.ageingDays + "d" : "—"}</td>
      </tr>`).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Lifecycle Explorer Export</title>
    <style>
      @page { size: A4 landscape; margin: 14mm; }
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111827; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .head { border-bottom: 2px solid #071A2F; padding-bottom: 12px; margin-bottom: 16px; }
      .eyebrow { font-size: 10px; letter-spacing: .08em; text-transform: uppercase; color: #2563EB; font-weight: 700; }
      h1 { font-size: 19px; margin: 4px 0 8px; color: #071A2F; letter-spacing: -0.01em; }
      .meta { font-size: 11px; color: #4B5563; line-height: 1.6; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      thead { display: table-header-group; }
      th { background: #071A2F; color: #fff; text-align: left; padding: 7px 8px; font-size: 9px; letter-spacing: .06em; text-transform: uppercase; font-weight: 700; }
      td { padding: 6px 8px; border-bottom: 1px solid #E5E7EB; vertical-align: top; }
      tr:nth-child(even) td { background: #F5F8FF; }
      .mono { font-family: 'SF Mono', ui-monospace, Consolas, monospace; font-weight: 700; color: #1D4ED8; white-space: nowrap; }
      .sum { max-width: 340px; }
      .nw { white-space: nowrap; }
      .foot { margin-top: 14px; font-size: 10px; color: #9CA3AF; }
    </style></head><body>
      <div class="head">
        <div class="eyebrow">Lifecycle Intelligence</div>
        <h1>Lifecycle Explorer — Ticket Export</h1>
        <div class="meta">
          Generated ${esc(new Date().toLocaleString())}<br>
          ${filterLine}<br>
          ${tickets.length} of ${total} tickets included
        </div>
      </div>
      <table>
        <thead><tr>
          <th>Ticket</th><th>Summary</th><th>Priority</th><th>Status</th><th>Assignee</th><th>Sprint</th><th>Created</th><th>Updated</th><th>Age</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="foot">Exported from JiraOps · Lifecycle Explorer</div>
    </body></html>`;

    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
    iframe.setAttribute("aria-hidden", "true");
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => { iframe.remove(); }, 1000);
      }
    };
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  }, [tickets, total, search, statusFilter, priorityFilter, assigneeFilter]);

  const hasFilters = search || statusFilter || priorityFilter || assigneeFilter;

  // One control style for the whole filter row — consistent height/type/border
  const ctl = {
    height: 36,
    boxSizing: "border-box",
    padding: "0 12px",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: 0,
    fontSize: 13,
    fontFamily: "inherit",
    color: "rgba(255,255,255,0.82)",
    background: "#0B223F",
    outline: "none",
  };

  return (
    <OverviewSection background="page" divider>
      <div style={{ borderRadius: 0, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>
        {/* Dark card header: title + count + filter bar */}
        <div style={{ background: "#071A2F" }}>
          <div style={{ padding: "28px 32px 0" }}>
            <SectionHeader
              light
              eyebrow="Lifecycle Intelligence"
              title="Lifecycle Explorer"
              description="Search and inspect tickets across all lifecycle stages."
              style={{ marginBottom: 0 }}
              actions={
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.02em", lineHeight: 1 }}>{total}</div>
                  <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>Total Tickets</div>
                </div>
              }
            />
          </div>
          <div style={{ padding: "20px 32px 24px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 24 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ticket number or summary…"
              style={{ ...ctl, flex: 1, minWidth: 240, background: "rgba(255,255,255,0.06)" }}
            />
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              style={ctl}
            >
              <option value="">All Statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={priorityFilter}
              onChange={e => { setPriorityFilter(e.target.value); setPage(0); }}
              style={ctl}
            >
              <option value="">All Priorities</option>
              {["Critical", "High", "Medium", "Low"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={assigneeFilter}
              onChange={e => { setAssigneeFilter(e.target.value); setPage(0); }}
              style={{ ...ctl, maxWidth: 180 }}
            >
              <option value="">All Developers</option>
              {developerOptions.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {hasFilters && (
              <button
                onClick={() => { setSearch(""); setStatusFilter(""); setPriorityFilter(""); setAssigneeFilter(""); }}
                style={{ ...ctl, color: "rgba(255,255,255,0.6)", background: "transparent", cursor: "pointer" }}
              >
                Clear
              </button>
            )}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
              {sprintSyncMsg && (
                <span style={{ fontSize: 12, color: sprintSyncMsg.startsWith("Updated") ? "#86EFAC" : "#FCA5A5" }}>{sprintSyncMsg}</span>
              )}
              <button
                onClick={exportPDF}
                disabled={tickets.length === 0}
                title="Open a print-ready view of the rows currently loaded below, then choose “Save as PDF” to share it."
                style={{ ...ctl, fontWeight: 600, color: tickets.length === 0 ? "rgba(255,255,255,0.3)" : "#93C5FD", background: "rgba(37,99,235,0.14)", border: "1px solid rgba(147,197,253,0.3)", cursor: tickets.length === 0 ? "not-allowed" : "pointer", whiteSpace: "nowrap", letterSpacing: "0.02em" }}
              >
                Export PDF
              </button>
              <button
                onClick={syncSprints}
                disabled={sprintSyncing}
                title="Pulls the latest sprint assignment for each ticket from Jira. Separate from the main Sync Jira action in the top nav."
                style={{ ...ctl, fontWeight: 600, color: sprintSyncing ? "rgba(255,255,255,0.3)" : "#93C5FD", background: "rgba(37,99,235,0.14)", border: "1px solid rgba(147,197,253,0.3)", cursor: sprintSyncing ? "not-allowed" : "pointer", whiteSpace: "nowrap", letterSpacing: "0.02em" }}
              >
                {sprintSyncing ? "Syncing…" : "Sync Sprints"}
              </button>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap" }}>
                {loading ? "Loading…" : `${tickets.length} of ${total} shown`}
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ background: "#071A2F", overflow: "hidden" }}>
          <div className="themed-scroll-dark" style={{ maxHeight: "min(65vh, 560px)", overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
              <thead>
                <tr style={{ background: "#040E1C" }}>
                  {[
                    { label: "Ticket" },
                    { label: "Summary" },
                    { label: "Priority" },
                    { label: "Status" },
                    { label: "Assignee" },
                    { label: "Sprint" },
                    { label: "Created" },
                    { label: "Updated" },
                    { label: "Age" },
                    { label: "Journey ⏳", dim: true, title: "Click a row to expand its full status history, ownership history, and linked pull requests." },
                  ].map(h => (
                    <th key={h.label} title={h.title} style={{
                      position: "sticky",
                      top: 0,
                      background: "#040E1C",
                      padding: "12px 16px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: h.dim ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.55)",
                      textTransform: "uppercase",
                      textAlign: "left",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                      whiteSpace: "nowrap",
                      letterSpacing: "0.07em",
                    }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: 13, background: "#071A2F" }}>
                      {hasFilters ? "No tickets match your filters." : "No tickets found."}
                    </td>
                  </tr>
                )}
                {tickets.map((t, i) => {
                  const ac = ageColor(t.ageingDays);
                  const rowBg = i % 2 === 0 ? "#071A2F" : "#0E2643";
                  const cell = { padding: "11px 16px", whiteSpace: "nowrap" };
                  return (
                    <React.Fragment key={t.ticketNo}>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: rowBg }}>
                      <td style={{ ...cell, borderLeft: "3px solid #3B82F6" }}>
                        <a href={`${JIRA}/${t.ticketNo}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: "#93C5FD", textDecoration: "none", fontSize: 13, fontFamily: TYPE.mono.fontFamily }}>
                          {t.ticketNo}
                        </a>
                      </td>
                      <td style={{ padding: "11px 16px", maxWidth: 300 }}>
                        <span title={t.summary || ""} style={{ fontSize: 13, color: "rgba(255,255,255,0.82)", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 290 }}>
                          {t.summary || "—"}
                        </span>
                      </td>
                      <td style={cell}>
                        <PriorityBadge priority={t.priority} />
                      </td>
                      <td style={cell}>
                        <StatusBadge status={t.status} />
                      </td>
                      <td style={{ ...cell, fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{t.assignedTo || "—"}</td>
                      <td style={{ ...cell, fontSize: 12, color: "rgba(255,255,255,0.55)" }}>{t.sprint || "—"}</td>
                      <td style={{ ...cell, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{fmtDate(t.raisedOn)}</td>
                      <td style={{ ...cell, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{fmtDate(t.updatedOn)}</td>
                      <td style={cell}>
                        {t.ageingDays != null ? (
                          <span style={{ background: ac.bg, color: ac.c, fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 0 }}>
                            {t.ageingDays}d
                          </span>
                        ) : <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>}
                      </td>
                      <td style={cell}>
                        <button
                          onClick={() => toggleTicket(t.ticketNo)}
                          style={{ background: expandedTicket === t.ticketNo ? "rgba(37,99,235,0.22)" : "none", border: "1px solid rgba(255,255,255,0.16)", color: "#93C5FD", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 0, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          {expandedTicket === t.ticketNo ? "Hide ↑" : "View ↓"}
                        </button>
                      </td>
                    </tr>
                    {expandedTicket === t.ticketNo && (
                      <tr>
                        <td colSpan={10} style={{ padding: 0 }}>
                          <TicketLifecyclePanel
                            data={lifecycleCache[t.ticketNo]}
                            loading={lifecycleLoading && expandedTicket === t.ticketNo}
                            prs={prCache[t.ticketNo]}
                            prsLoading={prLoading && expandedTicket === t.ticketNo}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tickets.length < total && (
            <div style={{ padding: 16, textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.08)", background: "#071A2F" }}>
              <button
                onClick={loadMore}
                disabled={loading}
                style={{ padding: "9px 28px", background: "rgba(37,99,235,0.22)", color: "#93C5FD", border: "1px solid rgba(147,197,253,0.3)", borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1, fontFamily: "inherit" }}
              >
                {loading ? "Loading…" : `Load More (${total - tickets.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      </div>
    </OverviewSection>
  );
}

// ─── 2. WORKFLOW DISTRIBUTION ─────────────────────────────────────────────────
function WorkflowDistribution() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [sortBy, setSortBy] = useState("count");

  useEffect(() => {
    fetch(`${API}/lifecycle/workflow-distribution`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <OverviewSection background="page" divider><Loading /></OverviewSection>
  );

  const statuses = data?.statuses || [];
  const total = data?.total || 0;
  const maxCount = Math.max(...statuses.map(s => s.count), 1);
  const sorted = [...statuses].sort((a, b) =>
    sortBy === "count" ? b.count - a.count : a.status.localeCompare(b.status)
  );
  const selected = statuses.find(s => s.status === selectedStatus) || null;

  const PIPELINE = [
    { key: "backlog", label: "Backlog",     sublabel: "To Do & Open",        color: "#94A3B8", bar: "#CBD5E1", light: "#F8FAFC" },
    { key: "active",  label: "In Progress", sublabel: "Active development",   color: "#64748B", bar: "#94A3B8", light: "#F1F5F9" },
    { key: "review",  label: "Review / QA", sublabel: "Pending sign-off",     color: "#475569", bar: "#64748B", light: "#EEF2F7" },
    { key: "done",    label: "Done",        sublabel: "Resolved & closed",    color: "#334155", bar: "#475569", light: "#E8EDF3" },
  ];
  const stageBarColor = (status) => {
    const cat = categorizeStage(status);
    const p = PIPELINE.find(x => x.key === cat);
    return p?.bar || "#94A3B8";
  };
  const pipeline = PIPELINE.map(p => {
    const stages = statuses.filter(s => categorizeStage(s.status) === p.key);
    const count  = stages.reduce((sum, s) => sum + s.count, 0);
    const pct    = total > 0 ? Math.round((count / total) * 100) : 0;
    return { ...p, count, pct, stages };
  });
  const wip = pipeline.filter(p => p.key !== "done").reduce((sum, p) => sum + p.count, 0);
  const pipelineMax = Math.max(...pipeline.map(p => p.count), 1);
  const bottleneck  = pipeline
    .filter(p => p.key === "active" || p.key === "review")
    .sort((a, b) => b.count - a.count)[0];
  const isBottleneck = bottleneck && wip > 0 && (bottleneck.count / wip) > 0.55;

  return (
    <OverviewSection background="page" divider>
      <div style={{ borderRadius: 0, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>

        {/* Dark header */}
        <div style={{ background: "#071A2F", padding: "28px 32px", borderBottom: "1px solid #1A3557" }}>
          <SectionHeader
            light
            eyebrow="Lifecycle Intelligence"
            title="Workflow Distribution"
            description={`Every raw Jira status grouped automatically into Backlog, In Progress, Review/QA, or Done by keyword. ${selected ? `Viewing: ${selected.status}.` : `${statuses.length} workflow stages tracked.`}`}
            style={{ marginBottom: 0 }}
            actions={
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 14 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.02em", lineHeight: 1 }}>{total}</div>
                  <div style={{ ...OVERVIEW.eyebrow, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>Total Tickets</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ key: "count", label: "By Volume" }, { key: "name", label: "A → Z" }].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      style={{
                        padding: "6px 12px", borderRadius: 0, fontSize: 11, fontWeight: 600, cursor: "pointer", letterSpacing: "0.04em", fontFamily: "inherit",
                        border: sortBy === opt.key ? "1px solid rgba(147,197,253,0.4)" : "1px solid rgba(255,255,255,0.14)",
                        background: sortBy === opt.key ? "rgba(37,99,235,0.22)" : "transparent",
                        color: sortBy === opt.key ? "#93C5FD" : "rgba(255,255,255,0.4)",
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
            }
          />
        </div>

        {/* Light body */}
        <div style={{ background: "#FFFFFF", padding: "32px" }}>
          {statuses.length === 0 ? (
            <div style={{ color: G.gray400, fontSize: 13, textAlign: "center", padding: 32 }}>No data available</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>

              {/* LEFT: Clickable stage bars */}
              <div>
                <div style={{ ...OVERVIEW.eyebrow, color: G.gray600, marginBottom: 16 }}>Stage Breakdown</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {sorted.map(s => {
                    const isSelected = s.status === selectedStatus;
                    return (
                      <div
                        key={s.status}
                        onClick={() => setSelectedStatus(isSelected ? null : s.status)}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#F8FAFC"; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                        style={{
                          padding: "11px 13px", borderRadius: 0, cursor: "pointer",
                          background: isSelected ? "#EEF4FF" : "transparent",
                          border: isSelected ? "1px solid #BFDBFE" : "1px solid transparent",
                          transition: "all 0.13s",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: isSelected ? "#2563EB" : "#CBD5E1", flexShrink: 0, transition: "background 0.13s" }} />
                            <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: isSelected ? "#071A2F" : "#334155", transition: "all 0.13s" }}>{s.status}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: isSelected ? "#071A2F" : "#334155" }}>{s.count}</span>
                            <span style={{ fontSize: 12, color: G.gray500, minWidth: 34, textAlign: "right" }}>{s.pct}%</span>
                          </div>
                        </div>
                        <div style={{ height: 5, background: "#F1F5F9", borderRadius: 0, overflow: "hidden" }}>
                          <div style={{ height: 5, width: `${(s.count / maxCount) * 100}%`, background: isSelected ? "#2563EB" : stageBarColor(s.status), borderRadius: 0, transition: "background 0.13s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: Pipeline Health (default) or Stage Detail (when selected) */}
              <div>
                {selected ? (
                  /* ── Stage Detail ── */
                  <div>
                    <button
                      onClick={() => setSelectedStatus(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#64748B", padding: 0, marginBottom: 20, display: "flex", alignItems: "center", gap: 5, fontFamily: "inherit" }}
                    >
                      ← Pipeline Health
                    </button>
                    <div style={{ border: "1px solid #BFDBFE", borderRadius: 0, padding: "24px", background: "#F8FAFC" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Stage Detail</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#071A2F", marginBottom: 4 }}>{selected.status}</div>
                      {(() => {
                        const cat = categorizeStage(selected.status);
                        const bucket = pipeline.find(p => p.key === cat);
                        return (
                          <div style={{ fontSize: 12, color: bucket.color, fontWeight: 600, marginBottom: 20 }}>
                            {bucket.label} stage
                          </div>
                        );
                      })()}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                        <div style={{ background: "#fff", border: "1px solid #BFDBFE", borderRadius: 0, padding: "14px 16px" }}>
                          <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Tickets here</div>
                          <div style={{ fontSize: 36, fontWeight: 800, color: "#071A2F", lineHeight: 1 }}>{selected.count}</div>
                        </div>
                        <div style={{ background: "#fff", border: "1px solid #BFDBFE", borderRadius: 0, padding: "14px 16px" }}>
                          <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Of total</div>
                          <div style={{ fontSize: 36, fontWeight: 800, color: "#2563EB", lineHeight: 1 }}>{selected.pct}%</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 7 }}>
                        Rank #{sorted.findIndex(s => s.status === selectedStatus) + 1} of {sorted.length} stages · vs. largest stage
                      </div>
                      <div style={{ height: 7, background: "#EEF4FF", borderRadius: 0, overflow: "hidden", marginBottom: 20 }}>
                        <div style={{ height: 7, width: `${(selected.count / maxCount) * 100}%`, background: "#2563EB", borderRadius: 0 }} />
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Other stages in same bucket</div>
                      {(() => {
                        const cat = categorizeStage(selected.status);
                        const peers = statuses.filter(s => categorizeStage(s.status) === cat && s.status !== selected.status);
                        return peers.length === 0
                          ? <div style={{ fontSize: 12, color: "#94A3B8" }}>No other stages in this bucket</div>
                          : peers.map(p => (
                            <div key={p.status} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", padding: "5px 0", borderBottom: "1px solid #EEF4FF" }}>
                              <span>{p.status}</span>
                              <span style={{ fontWeight: 600, color: "#071A2F" }}>{p.count}</span>
                            </div>
                          ));
                      })()}
                    </div>
                  </div>
                ) : (
                  /* ── Pipeline Health ── */
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 12, flexWrap: "wrap" }}>
                      <div style={{ ...OVERVIEW.eyebrow, color: G.gray600 }}>Pipeline Health</div>
                      <div style={{ fontSize: 13, color: G.gray600 }}>
                        <span style={{ fontWeight: 700, color: "#071A2F" }}>{wip}</span> tickets in flight
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: G.gray600, lineHeight: 1.55, marginBottom: 16 }}>
                      "In flight" (WIP) means every ticket not yet in the Done stage.
                    </div>

                    {isBottleneck && (
                      <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 0, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#C2410C" }}>
                        <strong>Bottleneck detected</strong> — {bottleneck.label} holds {Math.round((bottleneck.count / wip) * 100)}% of WIP
                        <ScoreBreakdown
                          title="How a bottleneck is detected"
                          factors={[{ label: "Active or Review stage holds more than", points: "55% of WIP" }]}
                          footnote="Checked against whichever of Active or Review has the larger share."
                          style={{ marginTop: 8 }}
                        />
                      </div>
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {pipeline.map((p, i) => (
                        <div key={p.key} style={{ background: p.light, border: `1px solid ${p.bar}33`, borderRadius: 0, padding: "16px 18px", minHeight: 92, boxSizing: "border-box" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.bar, flexShrink: 0 }} />
                                <span style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{p.label}</span>
                                {isBottleneck && bottleneck.key === p.key && (
                                  <span style={{ fontSize: 10, fontWeight: 700, background: "#FED7AA", color: "#C2410C", padding: "1px 6px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "0.05em" }}>bottleneck</span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: G.gray500, marginLeft: 15 }}>{p.sublabel} · {p.stages.length} stage{p.stages.length !== 1 ? "s" : ""}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 24, fontWeight: 800, color: p.color, lineHeight: 1 }}>{p.count}</div>
                              <div style={{ fontSize: 12, color: G.gray500, marginTop: 3 }}>{p.pct}%</div>
                            </div>
                          </div>
                          <div style={{ height: 6, background: `${p.bar}30`, borderRadius: 0, overflow: "hidden" }}>
                            <div style={{ height: 6, width: `${(p.count / pipelineMax) * 100}%`, background: p.bar, borderRadius: 0 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </OverviewSection>
  );
}

// ─── 3. LIFECYCLE OUTLIERS ────────────────────────────────────────────────────
function lightPriStyle(priority) {
  const p = (priority || "").toLowerCase();
  if (p === "critical") return { bg: "#FEE2E2", c: "#991B1B" };
  if (p === "high" || p === "highest") return { bg: "#DBEAFE", c: "#1D4ED8" };
  if (p === "medium") return { bg: "#EFF6FF", c: "#3B82F6" };
  return { bg: "#F1F5F9", c: "#64748B" };
}
function lightStatStyle(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("done") || s.includes("closed") || s.includes("resolved")) return { bg: "#DCFCE7", c: "#166534" };
  if (s.includes("progress") || s.includes("review") || s.includes("qa") || s.includes("test")) return { bg: "#DBEAFE", c: "#1D4ED8" };
  return { bg: "#F1F5F9", c: "#475569" };
}

function OutlierCard({ label, ticket, metric, detail }) {
  return (
    <div style={{ background: "#FFFFFF", border: `1px solid ${OVERVIEW.cardBorder}`, borderRadius: 0, padding: "20px 22px", display: "flex", flexDirection: "column", minHeight: 178, boxSizing: "border-box" }}>
      <div style={{ ...OVERVIEW.eyebrow, color: G.gray600, marginBottom: 12 }}>{label}</div>
      {!ticket ? (
        <div style={{ color: G.gray300, fontSize: 13, marginTop: 10 }}>No data</div>
      ) : (
        <>
          <div style={{ fontSize: 23, fontWeight: 800, color: "#071A2F", lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: 10, minHeight: 28 }}>{metric}</div>
          <a
            href={`${JIRA}/${ticket.ticketNo}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700, color: "#2563EB", textDecoration: "none", fontSize: 13, fontFamily: TYPE.mono.fontFamily, marginBottom: 10, display: "block" }}
          >
            {ticket.ticketNo}
          </a>
          <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.55, flex: 1, marginBottom: 14 }}>
            {(ticket.summary || "No summary").slice(0, 70)}{(ticket.summary || "").length > 70 ? "…" : ""}
          </div>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {ticket.priority && (() => { const s = lightPriStyle(ticket.priority); return <span style={{ fontSize: 10, fontWeight: 600, background: s.bg, color: s.c, padding: "2px 7px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>{ticket.priority}</span>; })()}
            {ticket.status && (() => { const s = lightStatStyle(ticket.status); return <span style={{ fontSize: 10, fontWeight: 500, background: s.bg, color: s.c, padding: "2px 7px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>{ticket.status}</span>; })()}
            {ticket.assignedTo && <span style={{ fontSize: 11, color: "#475569", background: "#F1F5F9", padding: "2px 7px", borderRadius: 0 }}>{ticket.assignedTo}</span>}
          </div>
          {detail && <div style={{ marginTop: 8, fontSize: 12, color: G.gray500 }}>{detail}</div>}
        </>
      )}
    </div>
  );
}

function LifecycleOutliers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/lifecycle/outliers`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <OverviewSection background="page" divider><Loading /></OverviewSection>
  );

  const { oldest, longestUnassigned, highestPriority, oldestReview, mostRecentlyUpdated } = data || {};

  return (
    <OverviewSection background="page" divider>
      <div style={{ borderRadius: 0, border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflow: "hidden" }}>

        {/* Dark card header */}
        <div style={{ background: "#071A2F", padding: "28px 32px", borderBottom: "1px solid #1A3557" }}>
          <SectionHeader
            light
            eyebrow="Lifecycle Intelligence"
            title="Lifecycle Outliers"
            description="Tickets at the extreme ends of key lifecycle dimensions — five single tickets, each the current record-holder on its own dimension, not a ranked list."
            style={{ marginBottom: 0 }}
          />
        </div>

        {/* Light card body */}
        <div style={{ background: "#FFFFFF", padding: "28px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14 }}>
            <OutlierCard label="Oldest Open" ticket={oldest} metric={oldest ? `${oldest.ageingDays}d old` : "—"} detail="Longest-open ticket still unresolved" />
            <OutlierCard label="Longest Unassigned" ticket={longestUnassigned} metric={longestUnassigned ? `${longestUnassigned.ageingDays}d no owner` : "—"} detail="No assignee on record" />
            <OutlierCard label="Highest Priority" ticket={highestPriority} metric={highestPriority ? `${highestPriority.ageingDays || 0}d old` : "—"} detail="Highest-priority ticket still open" />
            <OutlierCard label="Oldest In Review" ticket={oldestReview} metric={oldestReview ? `${oldestReview.ageingDays}d in review` : "—"} detail="Longest-waiting ticket in a review/QA stage" />
            <OutlierCard label="Most Recent Activity" ticket={mostRecentlyUpdated} metric={mostRecentlyUpdated ? fmtDate(mostRecentlyUpdated.updatedOn) : "—"} detail="Latest recorded activity" />
          </div>
        </div>
      </div>
    </OverviewSection>
  );
}

// ─── 6. RECENTLY CLOSED ───────────────────────────────────────────────────────
function RecentlyClosed() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/lifecycle/recently-closed?limit=30`)
      .then(r => r.json())
      .then(d => { setTickets(d.tickets || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const th = { padding: "12px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", color: G.gray500, textTransform: "uppercase", textAlign: "left", borderBottom: `1px solid ${G.border}`, whiteSpace: "nowrap" };
  const td = { padding: "11px 16px", whiteSpace: "nowrap" };

  return (
    <OverviewSection background="page" divider>
      <SectionHeader
        eyebrow="Lifecycle Intelligence"
        title="Recently Closed"
        description="Latest resolved tickets, ordered by most recent activity. Lifecycle is the total number of days from when a ticket was created to when it was closed."
      />
      {loading ? <Loading /> : tickets.length === 0 ? (
        <div style={{ ...card(), border: `1px solid ${OVERVIEW.cardBorder}` }}>
          <div style={{ color: G.gray400, fontSize: 13 }}>No closed tickets found.</div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${OVERVIEW.cardBorder}`, boxShadow: OVERVIEW.cardShadow, overflowX: "auto", background: G.white }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 740 }}>
            <thead>
              <tr style={{ background: G.gray50 }}>
                {["Ticket", "Summary", "Priority", "Assignee", "Status", "Sprint", "Lifecycle", "Closed"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.map((t, i) => {
                const lc = t.lifecycleDays;
                const lcCol = lc == null
                  ? { bg: G.gray100, c: G.gray500 }
                  : lc > 14 ? { bg: G.red50, c: G.red700 }
                  : lc > 7 ? { bg: G.amber50, c: G.amber700 }
                  : { bg: G.green50, c: G.green700 };
                return (
                  <tr key={t.ticketNo} style={{ background: i % 2 === 0 ? G.white : G.gray50, borderBottom: `1px solid ${G.border}` }}>
                    <td style={td}>
                      <a href={`${JIRA}/${t.ticketNo}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: "#2563EB", textDecoration: "none", fontSize: 13, fontFamily: TYPE.mono.fontFamily }}>
                        {t.ticketNo}
                      </a>
                    </td>
                    <td style={{ padding: "11px 16px", maxWidth: 300 }}>
                      <span title={t.summary || ""} style={{ fontSize: 13, color: G.gray700, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 290 }}>
                        {t.summary || "—"}
                      </span>
                    </td>
                    <td style={td}>
                      <PriorityBadge priority={t.priority} />
                    </td>
                    <td style={{ ...td, fontSize: 13, color: G.gray700 }}>{t.assignedTo || "—"}</td>
                    <td style={td}>
                      {t.status ? (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", background: G.green50, color: G.green700 }}>{t.status}</span>
                      ) : <span style={{ color: G.gray300 }}>—</span>}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: G.gray500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }} title={t.sprint || ""}>{t.sprint || "—"}</td>
                    <td style={td}>
                      {lc != null ? (
                        <span style={{ background: lcCol.bg, color: lcCol.c, fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 0 }}>{lc}d</span>
                      ) : <span style={{ color: G.gray300 }}>—</span>}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: G.gray500 }}>
                      {fmtDate(t.resolutionDate || t.updatedOn)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </OverviewSection>
  );
}

// ─── LIFECYCLE AI ASSISTANT ───────────────────────────────────────────────────
const LC_PROMPT_GROUPS = [
  {
    category: "Bottlenecks",
    prompts: [
      "Where are tickets getting stuck in the workflow?",
      "Which stages have the longest average dwell time?",
    ],
  },
  {
    category: "Team Performance",
    prompts: [
      "Who is closing the most tickets this sprint?",
      "Which developer has the fastest turnaround time?",
    ],
  },
  {
    category: "Quality & Rework",
    prompts: [
      "Which ticket types regress the most often?",
      "What patterns exist in bounced or re-opened tickets?",
    ],
  },
  {
    category: "Risk & Delivery",
    prompts: [
      "Are there tickets older than 30 days still open?",
      "Which tickets are at risk of missing the sprint goal?",
    ],
  },
];

function LifecycleAIAssistant() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  const sendMessage = useCallback((text) => {
    const q = text || input.trim();
    if (!q) return;
    setInput("");
    setSelectedPrompt(q);
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setThinking(true);
    setTimeout(() => {
      setThinking(false);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "AI integration is coming soon. Once connected, I'll analyse your live Jira lifecycle data to answer this question with real-time insights, trend analysis, and actionable recommendations.",
      }]);
    }, 1200);
  }, [input]);

  const FF = FONT_FAMILY;

  return (
    <OverviewSection background="dark" divider style={{ fontFamily: FF }}>

      {/* Header */}
      <SectionHeader
        light
        eyebrow="AI Intelligence"
        title="Lifecycle Assistant"
        description="Ask questions about ticket flow, team performance, bottlenecks, and delivery risk."
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "6px 14px", flexShrink: 0 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(255,255,255,0.2)" }} />
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontWeight: 600, letterSpacing: "0.04em" }}>AI Integration Coming Soon</span>
          </div>
        }
      />

      {/* 2-column body */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.8fr", gap: 24, alignItems: "start" }}>

        {/* LEFT — Grouped prompts */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {LC_PROMPT_GROUPS.map(group => (
            <div key={group.category}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginBottom: 8 }}>
                {group.category}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {group.prompts.map(p => (
                  <div
                    key={p}
                    onClick={() => sendMessage(p)}
                    onMouseEnter={e => { if (selectedPrompt !== p) { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; } }}
                    onMouseLeave={e => { if (selectedPrompt !== p) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; } }}
                    style={{
                      padding: "10px 14px",
                      background: selectedPrompt === p ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.04)",
                      border: selectedPrompt === p ? "1px solid rgba(37,99,235,0.5)" : "1px solid rgba(255,255,255,0.07)",
                      borderRadius: 6, cursor: "pointer", fontSize: 13,
                      color: selectedPrompt === p ? "#93C5FD" : "rgba(255,255,255,0.5)",
                      lineHeight: 1.45, transition: "all 0.1s",
                    }}
                  >
                    {p}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT — Chat panel */}
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {/* Response area */}
          <div style={{ flex: 1, minHeight: 360, display: "flex", flexDirection: "column", justifyContent: messages.length === 0 ? "center" : "flex-start", alignItems: messages.length === 0 ? "center" : "stretch", padding: "32px 32px 24px", overflowY: "auto" }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 13, color: "#93C5FD", fontWeight: 700 }}>
                  AI
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Ready to assist</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.25)", lineHeight: 1.6, maxWidth: 300 }}>
                  Select a suggested prompt on the left or type your own question below.
                </div>
              </div>
            ) : (
              <div style={{ width: "100%" }}>
                {messages.map((m, i) => (
                  <div key={i} style={{ marginBottom: 20 }}>
                    {m.role === "user" ? (
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <div style={{ background: "#2563EB", borderRadius: "12px 12px 2px 12px", padding: "12px 18px", maxWidth: "80%", fontSize: 13, color: "#FFFFFF", lineHeight: 1.55 }}>
                          {m.text}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(37,99,235,0.2)", border: "1px solid rgba(37,99,235,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 11, color: "#93C5FD", fontWeight: 700 }}>
                          AI
                        </div>
                        <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px 12px 12px 2px", padding: "14px 18px", fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6, fontStyle: "italic" }}>
                          {m.text}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {thinking && (
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(37,99,235,0.2)", border: "1px solid rgba(37,99,235,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 11, color: "#93C5FD", fontWeight: 700 }}>AI</div>
                    <div style={{ display: "flex", gap: 5, alignItems: "center", padding: "14px 18px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px 12px 12px 2px" }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "rgba(255,255,255,0.3)" }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: "rgba(255,255,255,0.07)" }} />

          {/* Input bar */}
          <div style={{ padding: "16px 20px", display: "flex", gap: 12, alignItems: "flex-end" }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask about lifecycle bottlenecks, team performance, sprint risk…"
              rows={2}
              style={{ flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 0, padding: "10px 12px", fontSize: 13, fontFamily: FF, resize: "none", outline: "none", lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || thinking}
              style={{ padding: "0 22px", height: 40, background: input.trim() && !thinking ? "#2563EB" : "rgba(255,255,255,0.06)", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 600, color: input.trim() && !thinking ? "#FFFFFF" : "rgba(255,255,255,0.25)", fontFamily: FF, cursor: input.trim() && !thinking ? "pointer" : "not-allowed", flexShrink: 0, transition: "all 0.12s" }}
            >
              {thinking ? "…" : "Ask"}
            </button>
          </div>
        </div>
      </div>
    </OverviewSection>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function LifecycleAnalysisDashboard() {
  return (
    <div style={{ fontFamily: FONT_FAMILY, background: "#F8FAFC", minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <Sidebar />
      <div style={{ marginLeft: SIDEBAR_WIDTH }}>
        <SectionNav sections={LC_SECTIONS} topOffset={0} />
        <div id="lc-overview" style={LC_ANCHOR}><PageHeader /></div>
        <div id="lc-explorer" style={LC_ANCHOR}><LifecycleExplorer /></div>
        <div id="lc-distribution" style={LC_ANCHOR}><WorkflowDistribution /></div>
        <div id="lc-outliers" style={LC_ANCHOR}><LifecycleOutliers /></div>
        <div id="lc-recently-closed" style={LC_ANCHOR}><RecentlyClosed /></div>
        <div id="lc-assistant" style={LC_ANCHOR}><LifecycleAIAssistant /></div>
      </div>
    </div>
  );
}
